/**
 * Media reconciliation registry (BAL-657, Layer 2).
 *
 * Maps each Supabase Storage bucket to the table that owns its rows, so ONE
 * registry-driven cron can reconcile every producer's output — web-TS,
 * signed-URL browser PUTs, and mobile-Dart — uniformly, because it reads final
 * DB + storage state, not the write path.
 *
 * SAFETY: a wrong entry here (wrong table or path column) would make every
 * row look lost and hide it from consumers. The cron only REPORTS objects with
 * no owning row; it does not delete them, because other tables (profiles
 * avatars, jsonb form blobs, r2_key) can still reference them. Every entry below is verified against
 * prod (2026-07-08): the table exists, has a `storage_path` column, and stores
 * rows for exactly this one bucket. Multi-bucket tables (`document_metadata`,
 * legacy `media`) are DELIBERATELY EXCLUDED — reconciling them needs per-row
 * bucket resolution and is out of this net until modelled explicitly.
 *
 * Lost-state columns exist on every listed table (migration
 * 20260726000001_bal657_media_lost_state_columns.sql; artist_media had them
 * already). The row-no-object direction sets them; consumers filter on
 * `requires_reupload`.
 */

export interface MediaReconciliationPair {
  /** Supabase Storage bucket id. */
  bucket: string;
  /** public table that owns rows for this bucket. */
  table: string;
  /** column on `table` holding the object path within `bucket`. */
  pathColumn: string;
  /** boolean flag set true when the object is missing. */
  lostFlagColumn: string;
  /** timestamptz set when the row was flagged lost. */
  lostAtColumn: string;
  /** text reason (e.g. 'object_missing'). */
  lostReasonColumn: string;
}

/**
 * Grace window (ms) before an object with no owning row is considered an
 * orphan. Covers the legitimate gap between a byte upload and its row insert
 * (signed-URL finalize, mobile insert, slow request), so a mid-finalize object
 * is not reported.
 */
export const RECONCILIATION_OBJECT_GRACE_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Reason string written to `lostReasonColumn` when a row's object is missing.
 */
export const LOST_REASON_OBJECT_MISSING = 'object_missing';

/**
 * Verified single-bucket pairs. `storage_path` is the path column on every one.
 */
export const MEDIA_RECONCILIATION_REGISTRY: readonly MediaReconciliationPair[] =
  [
    pair('dancer-media', 'artist_media'),
    pair('reimbursements', 'reimbursement_documents'),
    pair('profile-media', 'profile_media'),
    pair('organization-media', 'organization_media'),
    pair('venue-documents', 'venue_documents'),
    pair('production-documents', 'production_documents'),
    pair('costume-photos', 'costume_photos'),
    pair('marketplace-images', 'marketplace_listing_images'),
  ] as const;

/** Build a pair with the standard lost-state column names. */
function pair(bucket: string, table: string): MediaReconciliationPair {
  return {
    bucket,
    table,
    pathColumn: 'storage_path',
    lostFlagColumn: 'requires_reupload',
    lostAtColumn: 'lost_at',
    lostReasonColumn: 'lost_reason',
  };
}
